/**
 * /legal/security — Security disclosure.
 */

import { createFileRoute } from '@tanstack/react-router';
import { LegalLayout } from '../../components/layouts/legal-layout';

export const Route = createFileRoute('/legal/security')({
  component: SecurityPage,
});

const TOC = [
  { anchor: 'residency',      label: 'Data residency' },
  { anchor: 'encryption',     label: 'Encryption' },
  { anchor: 'audit-chain',    label: 'Audit chain integrity' },
  { anchor: 'incident',       label: 'Incident response' },
  { anchor: 'vuln-disclosure', label: 'Vulnerability disclosure' },
];

/**
 * PGP key fingerprint shown on the security disclosure page.
 *
 * Source of truth: the public-key block in
 *   frontend/public/.well-known/security-pgp.txt
 *
 * When Paul generates the keypair per the runbook at
 *   docs-vault/07-runbooks/security/pgp-keypair-generation.md
 * he must replace BOTH this constant AND that public-key file in the
 * same commit, otherwise the fingerprint shown to security researchers
 * will not match the public key they download.
 *
 * Leaving the empty string is the deliberate "not yet published" signal
 * that triggers the in-progress notice in <PgpKeyBlock />. Do NOT
 * substitute a fake / placeholder fingerprint here — researchers will
 * try to verify it and a mismatch is worse than a "we're working on it"
 * disclosure.
 */
export const PGP_KEY_FINGERPRINT = 'E4925E048F395BABDB4B14C61BCE5573511BCD66';

function SecurityPage() {
  return (
    <LegalLayout heading="Security disclosure." lastUpdated="7 May 2026" toc={TOC}>
      <p>
        Trust records are irreplaceable. Our security design starts from that premise, not from
        minimum compliance. This page describes the controls in place.
      </p>

      <h2 id="residency">Data residency</h2>
      <p>
        All primary data is stored in <strong>Microsoft Azure South Africa North</strong> (Johannesburg).
        Geo-redundant backups replicate to <strong>Azure South Africa West</strong> (Cape Town). No
        production trust data is stored outside South Africa, in accordance with section 72(1)(c)
        of POPIA.
      </p>
      <div className="legal-info-box">
        <div className="eyebrow">Residency summary</div>
        <table className="legal-table" style={{ margin: 0 }}>
          <thead>
            <tr>
              <th>Data category</th>
              <th>Primary region</th>
              <th>Fallback region</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Trust records, resolutions, audit trail</td>
              <td>Azure South Africa North</td>
              <td>Azure South Africa West</td>
            </tr>
            <tr>
              <td>Document vault (FICA, deed documents)</td>
              <td>Azure Blob SA North</td>
              <td>Azure Blob SA West (GRS)</td>
            </tr>
            <tr>
              <td>Authentication metadata</td>
              <td>Supabase EU West (Ireland)</td>
              <td>Supabase managed HA</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h2 id="encryption">Encryption</h2>
      <h3>Encryption at rest</h3>
      <p>
        All data stored in Azure Cosmos DB and Azure Blob Storage is encrypted at rest using
        AES-256 with Microsoft-managed keys. On top of that at-rest layer, our architecture adds
        field-level encryption (Azure Cosmos DB&apos;s Always Encrypted feature) for the most
        sensitive fields (beneficial-ownership records and FICA document metadata), encrypting
        them client-side before they reach the database engine. This field-level layer activates at
        general availability, once the customer-managed key hierarchy is provisioned; when live, the
        database layer sees only ciphertext for those fields.
      </p>

      <h3>Encryption in transit</h3>
      <p>
        All data in transit is encrypted using TLS 1.2 or higher. TLS 1.0 and 1.1 are disabled
        across all endpoints. HTTP-to-HTTPS redirects are enforced with HSTS (max-age 31536000).
      </p>

      <h2 id="audit-chain">Audit chain integrity</h2>
      <p>
        Every action on a trust (resolution signings, document uploads, trustee changes,
        distribution authorisations) is recorded in an immutable audit trail. Records are written
        to Azure Blob Storage with WORM (write-once, read-many) immutability policy enforced at the
        container level. Existing records cannot be modified or deleted, even by Elreign staff.
      </p>
      <p>
        This design satisfies the record-keeping requirements of section 23 of the Financial
        Intelligence Centre Act (minimum five-year retention), and the Trust Property Control Act.
      </p>
      <p>
        Each audit record carries a SHA-256 hash of its content and a chain hash linking it to the
        preceding record. Any tampering with an individual record breaks the chain and is detectable
        on verification.
      </p>

      <h2 id="incident">Incident response</h2>
      <p>
        Elreign maintains a written incident response plan with the following SLAs:
      </p>
      <table className="legal-table">
        <thead>
          <tr>
            <th>Severity</th>
            <th>Definition</th>
            <th>Response SLA</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>SEV-1</td>
            <td>Data breach with likely personal information exposure, or full platform outage</td>
            <td>15 minutes to acknowledge; 1 hour to initial status update</td>
          </tr>
          <tr>
            <td>SEV-2</td>
            <td>Partial platform degradation, or suspected (unconfirmed) breach</td>
            <td>1 hour to acknowledge; 4 hours to initial status update</td>
          </tr>
          <tr>
            <td>SEV-3</td>
            <td>Minor service issues with no personal information impact</td>
            <td>4 hours to acknowledge; next business day update</td>
          </tr>
        </tbody>
      </table>
      <p>
        In the event of a personal information breach, affected responsible parties (tenant firms)
        will be notified within 72 hours in accordance with POPIA s22, to enable them to fulfil
        their own notification obligations to data subjects.
      </p>

      <h2 id="vuln-disclosure">Vulnerability disclosure</h2>
      <p>
        If you discover a security vulnerability in Elreign, we ask that you disclose it
        responsibly. Do not exploit the vulnerability or access data beyond what is necessary to
        demonstrate the issue.
      </p>
      <p>
        Report vulnerabilities to{' '}
        <a href="mailto:security@elreign.io" style={{ color: 'var(--primary-600)' }}>
          security@elreign.io
        </a>
        . You may encrypt your report to our PGP key:
      </p>
      <PgpKeyBlock />
      <p style={{ fontSize: 13, color: 'var(--ink-3)', margin: '0 0 18px' }}>
        Public key available at{' '}
        <a
          href="/.well-known/security-pgp.txt"
          style={{ color: 'var(--primary-600)' }}
          data-testid="pgp-key-download"
        >
          /.well-known/security-pgp.txt
        </a>
        {' '}and on{' '}
        <a
          href="https://keys.openpgp.org/search?q=security%40elreign.io"
          target="_blank"
          rel="noreferrer noopener"
          style={{ color: 'var(--primary-600)' }}
        >
          keys.openpgp.org
        </a>
        .
      </p>
      <p>
        We aim to acknowledge receipt within 1 business day, and to provide a resolution timeline
        within 5 business days. We do not operate a bug bounty programme at this stage, but we
        will credit researchers in our security disclosure history with their permission.
      </p>
    </LegalLayout>
  );
}

/**
 * Formats a 40-character hex fingerprint into five-character groups
 * separated by spaces, broken across two lines with a middle dot —
 * the OpenPGP canonical display format used on keys.openpgp.org.
 *
 * Example: "ABCD1234EF...90" → "ABCD1 234EF ... 0123 4567 89AB · ..."
 */
export function formatPgpFingerprint(fp: string): string {
  const hex = fp.replace(/\s+/g, '').toUpperCase();
  if (hex.length !== 40) return fp;
  const groups: string[] = [];
  for (let i = 0; i < 40; i += 4) groups.push(hex.slice(i, i + 4));
  const half = groups.length / 2;
  return `${groups.slice(0, half).join(' ')} · ${groups.slice(half).join(' ')}`;
}

/**
 * Renders the PGP fingerprint block on /legal/security. Two states:
 *
 *   1. Fingerprint published (PGP_KEY_FINGERPRINT non-empty): shows the
 *      formatted fingerprint + a "verify on keys.openpgp.org" note.
 *
 *   2. Publication in progress (PGP_KEY_FINGERPRINT empty): shows an
 *      honest in-progress notice with a link to download whatever is
 *      currently in /.well-known/security-pgp.txt (which today is the
 *      placeholder file with instructions for Paul). This avoids the
 *      "XXXX XXXX XXXX XXXX" placeholder anti-pattern that the audit
 *      flagged on the previous revision.
 *
 *  The component is exported so the security disclosure test can render
 *  it directly without standing up the full route tree.
 */
export function PgpKeyBlock() {
  const published = PGP_KEY_FINGERPRINT.length > 0;
  return (
    <div
      className="mono"
      data-testid="pgp-key-block"
      style={{
        background: 'var(--surface-2)',
        border: '1px solid var(--hairline)',
        borderRadius: 8,
        padding: '14px 18px',
        fontSize: 13,
        color: 'var(--ink)',
        letterSpacing: '0.02em',
        lineHeight: 1.6,
        margin: '0 0 18px',
      }}
    >
      {published ? (
        <>
          <span data-testid="pgp-fingerprint">
            Key fingerprint: {formatPgpFingerprint(PGP_KEY_FINGERPRINT)}
          </span>
          <br />
          <span style={{ color: 'var(--ink-3)', fontSize: 12 }}>
            Verify on keys.openpgp.org before encrypting a sensitive report.
          </span>
        </>
      ) : (
        <>
          <span data-testid="pgp-in-progress">
            Key publication in progress.
          </span>
          <br />
          <span style={{ color: 'var(--ink-3)', fontSize: 12 }}>
            If you need to encrypt a sensitive report before the key is
            published, request the current key out-of-band from{' '}
            <a
              href="mailto:security@elreign.io?subject=PGP%20key%20request"
              style={{ color: 'var(--primary-600)' }}
            >
              security@elreign.io
            </a>
            .
          </span>
        </>
      )}
    </div>
  );
}
